import React from "react";
import { t } from "i18next";
import { useSelector } from "react-redux";
import { Icon, Popup } from "semantic-ui-react";

import useTheme from "../../../../hooks/useTheme";
import {
  getFeeMatrixForWorkplace,
  getJobTotalHours,
} from "../../../../utils/feeFunctions";
import { getMoney, getNumber } from "../../../../utils/helperFunctions";
import { AppColumn, AppRow } from "../../../UI/Layouts";
import AppText from "../../../UI/Texts/AppTexts";
import { CompensationExplanationContent } from "../../../Jobs/JobDetails/Components/ThingsToKnowJob";

const ApplicationCompensationSummary = ({ jobData, compact = false }) => {
  const theme = useTheme();
  const feeConfigs = useSelector((state) => state.core.feeConfigs) ?? {};
  const totalHours = getJobTotalHours(jobData);
  const {
    canDisplayCompensation,
    compensationFeeMin,
    compensationFeeMax,
  } = getFeeMatrixForWorkplace({
    baseJobFeeAmount: jobData?.baseFee?.amount ?? 0,
    workplaceId: jobData?.workplaceDetails?.workPlaceId,
    feeConfigs,
    totalHours,
  });

  if (!canDisplayCompensation || totalHours <= 0) return null;

  const minTotal = compensationFeeMin * totalHours;
  const maxTotal = compensationFeeMax * totalHours;
  const compensation =
    minTotal === maxTotal
      ? getMoney(minTotal)
      : `${getMoney(minTotal)} - ${getMoney(maxTotal)}`;
  const sectionSpacing = compact
    ? theme.spacing.compactElementPadding
    : theme.spacing.elementPadding;

  return (
    <AppColumn
      style={{
        borderTop: `1px solid ${theme.colors.lightGrey}`,
        borderBottom: `1px solid ${theme.colors.lightGrey}`,
        gap: theme.spacing.elementPaddingSmall,
        padding: `${theme.spacing.elementPaddingSmall} 0`,
        marginBottom: sectionSpacing,
      }}
    >
      <AppRow mainAxisAlignment="space-between" crossAxisAlignment="center">
        <AppText.Footnote color={theme.colors.textSecondary}>
          {t("assignment_working_hours")}
        </AppText.Footnote>
        <AppText.Footnote fontWeight={600}>
          {`${getNumber(totalHours)} ${t("hours").toLowerCase()}`}
        </AppText.Footnote>
      </AppRow>
      <AppRow mainAxisAlignment="space-between" crossAxisAlignment="center">
        <AppRow crossAxisAlignment="center">
          <AppText.Footnote color={theme.colors.textSecondary}>
            {t("jobdetail.application.compensation")}
          </AppText.Footnote>
          <Popup
            basic
            on="click"
            position="top center"
            wide="very"
            content={<CompensationExplanationContent showIcon={false} />}
            trigger={
              <button
                type="button"
                aria-label={t("jobdetail.application.compensation_info")}
                style={{
                  background: "none",
                  border: "none",
                  color: theme.primaryColor,
                  cursor: "pointer",
                  padding: 0,
                  marginLeft: theme.spacing.elementPaddingSmall,
                }}
              >
                <Icon fitted name="info circle" />
              </button>
            }
          />
        </AppRow>
        <AppText.Footnote fontWeight={600} style={{ textAlign: "right" }}>
          {compensation}
        </AppText.Footnote>
      </AppRow>
    </AppColumn>
  );
};

export default ApplicationCompensationSummary;
